import { useCallback, useEffect, useRef } from "react";
import { KitchenWebSocket } from "../api/ws";
import type { KitchenEvent } from "@shared/types/events";

/**
 * Hook that manages a KitchenWebSocket connection lifecycle.
 * Connects on mount, disconnects on unmount.
 * Call onEvent to register listeners.
 */
export function useKitchenWebSocket(token: string | null) {
  const wsRef = useRef<KitchenWebSocket | null>(null);

  useEffect(() => {
    if (!token) return;

    const ws = new KitchenWebSocket(token);
    ws.connect();
    wsRef.current = ws;

    return () => {
      ws.disconnect();
      wsRef.current = null;
    };
  }, [token]);

  // Stable reference: wrapping in useCallback prevents useKitchenRealtime's
  // useEffect from tearing down and re-registering all listeners on every
  // parent render, which was causing spurious re-subscriptions and, in
  // StrictMode, racing with the WebSocket effect's wsRef.current assignment.
  const onEvent = useCallback(
    (
      eventType: string,
      handler: (event: KitchenEvent) => void,
    ): (() => void) => {
      if (!wsRef.current) return () => {};
      return wsRef.current.on(eventType, handler);
    },
    [], // wsRef is a stable ref object — no dependencies needed
  );

  return { onEvent, ws: wsRef };
}
